import gsap from 'gsap';
import { Container, Sprite } from 'pixi.js';

import { App, Shared } from '../App';
import LocalisedText from './LocalisedText';
import { Widget } from './Widget';

const defaultOpts = {
    dots: 3,
    spacing: 20,
};

export type OpponentLoaderOpts = typeof defaultOpts;

type IconPair = {
    base: Sprite;
    top: Sprite;
};

export class OpponentLoaderWidget extends Widget
{
    public label!: LocalisedText;

    protected icons: IconPair[];

    private _dots: number;
    private _spacing: number;
    private _currentScaleAnim: gsap.core.Tween | undefined;
    private _currentAlphaAnim: gsap.core.Tween | undefined;

    constructor(app: App, opts: Partial<OpponentLoaderOpts> = {})
    {
        super();
        const o = { ...defaultOpts, ...opts };

        this.icons = [] as IconPair[];
        this._dots = o.dots > 0 ? o.dots : 0;
        this._spacing = o.spacing;
    }

    protected async build(): Promise<void>
    {
        if (this._built) return;

        this.label = new LocalisedText(`game_loadingOpponents`, {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 8,
            lineJoin: 'round',
            align: 'center',
            fontSize: 42,
        });
        this.label.anchor.set(0.5);
        this.label.y = -40;
        this.label.x = 0;
        this.view.addChild(this.label);

        const dotContainer = new Container();

        this.view.addChild(dotContainer);

        let offset = 0;

        for (let i = 0; this._dots > i; i++)
        {
            const icons: IconPair = {
                base: new Sprite(Shared.assets.getTexture('loading-dot-grey')),
                top: new Sprite(Shared.assets.getTexture('loading-dot-white')),
            };

            this.icons.push(icons);

            icons.base.scale.set(0.5);
            icons.top.scale.set(0.5);
            icons.base.anchor.set(0.5);
            icons.top.anchor.set(0.5);
            icons.base.x = offset;
            icons.top.x = offset;

            icons.top.alpha = 0;

            dotContainer.addChild(icons.base);
            dotContainer.addChild(icons.top);

            offset += this._spacing + icons.base.width;
        }

        this._built = true;

        dotContainer.x = -(offset - this._spacing) / 2;
        dotContainer.y = 20;
    }

    protected async onOpen(): Promise<void>
    {
        this.icons.forEach((icons) =>
        {
            icons.base.scale.set(0.5);
            icons.top.scale.set(0.5);
        });

        this._animateDot(0);
    }

    protected async onClose(): Promise<void>
    {
        if (this._currentScaleAnim) this._currentScaleAnim.kill();
        if (this._currentAlphaAnim) this._currentAlphaAnim.kill();
    }

    private _animateDot(index: number): void
    {
        const icons = this.icons[index];
        const halfDur = 0.4;

        this._currentScaleAnim = gsap.to([icons.base.scale, icons.top.scale], {
            duration: halfDur,
            x: 0.55,
            y: 0.55,
            onComplete: () =>
            {
                this._currentScaleAnim = gsap.to([icons.base.scale, icons.top.scale], {
                    duration: halfDur,
                    x: 0.5,
                    y: 0.5,
                    onComplete: () =>
                    {
                        this._animateDot((index + 1) % this.icons.length);
                    },
                });
            },
        });

        this._currentAlphaAnim = gsap.to(icons.top, {
            duration: halfDur,
            alpha: 1,
            onComplete: () =>
            {
                this._currentAlphaAnim = gsap.to(icons.top, {
                    duration: halfDur,
                    alpha: 0,
                    onComplete: () =>
                    {
                        this._currentAlphaAnim = undefined;
                    },
                });
            },
        });
    }
}
